import { Suspense, useEffect } from "react";
import useAdjustViewport from "./utils/useAdjustViewport.js";
import { lazyWithRetry } from "./utils/lazyWithRetry.js";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import DiscoveryHome from "./components/discovery/pages/DiscoveryHome.js";
import { withShell } from "./components/discovery/withShell.js";
import LoadingAnimation from "./components/blocks/LoadingAnimation.js";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getPageRoute } from "src/services";
import { isCalendarPersonalizationEnabled } from "src/services/Config.js";
import {
  PreventBackButton,
  BackNavigationPosition,
  ScrollToTop,
} from "src/utils/NavigationUtils.js";
import { NavigationManager } from "src/utils/NavigationManager.js";
import { RedirectSignInForward } from "src/utils/RedirectSignInForward.js";
import { FeatureAccessProvider } from "./utils/FeatureAccessContext.js";
import CalendarLimitInterstitial from "./components/upgrades/consumer/CalendarLimitInterstitial.js";
import CalendarReviewInterstitial from "./components/blocks/reviews/CalendarReviewInterstitial.js";
import GoogleConnectInactiveInterstitial from "./components/blocks/googleconnect/GoogleConnectInactiveInterstitial.js";
import GoogleConnectInterceptHost from "./components/blocks/googleconnect/GoogleConnectInterceptHost.js";
import EmailLinkConfirmDialog from "./components/blocks/dialogs/EmailLinkConfirmDialog.js";

const lazyNamed = (loader, exportName) => lazyWithRetry(loader, exportName);

const lazy = (loader) => lazyWithRetry(loader);

const ImportCalendar = lazy(() => import("./components/ImportCalendar.js"));
const Timeline = lazy(() => import("./components/Timeline.js"));
const NotFound = lazy(() => import("./components/NotFound.js"));
const CalendarSettings = lazy(() => import("./components/CalendarSettings.js"));
const SignIn = lazy(() => import("./components/SignIn.js"));
const Library = lazy(() => import("./components/Library.js"));
const Search = lazy(() => import("./components/Search.js"));
const Privacy = lazy(() => import("./components/Privacy.js"));
const Terms = lazy(() => import("./components/Terms.js"));
const Vision = lazy(() => import("./components/Vision.js"));
const ProtectedRoute = lazyNamed(
  () => import("./utils/ProtectedRoute.js"),
  "ProtectedRoute",
);
const ConsumerUpgrade = lazy(
  () => import("./components/upgrades/consumer/ConsumerUpgrade.js"),
);
const MembershipPage = lazy(
  () => import("./components/upgrades/consumer/MembershipPage.js"),
);
const HowToImport = lazy(
  () => import("./components/instructions/HowToImport.js"),
);
const GoogleCalendarExperience = lazy(
  () =>
    import("./components/instructions/google-calendar/GoogleCalendarExperience.js"),
);
const CalendarExperience = lazy(
  () =>
    import("./components/instructions/other-calendars/CalendarExperience.js"),
);
const CreatorOnboardingAddLink = lazy(
  () =>
    import("./components/instructions/creator-onboarding/CreatorOnboardingAddLink.js"),
);
const CreatorOnboardingVerify = lazy(
  () =>
    import("./components/instructions/creator-onboarding/CreatorOnboardingVerify.js"),
);
const ConnectGoogleCalendar = lazy(
  () =>
    import("./components/instructions/google-calendar/ConnectGoogleCalendar.js"),
);
const TurnOffGoogleSync = lazy(
  () =>
    import("./components/instructions/google-calendar/TurnOffGoogleSync.js"),
);
const TimelineEmbedded = lazyNamed(
  () => import("./components/TimelineEmbedded.js"),
  "TimelineEmbedded",
);
const GoogleCalendarCallback = lazy(
  () =>
    import("./components/instructions/google-calendar/GoogleCalendarCallback.js"),
);
const GoogleCalendarSteps = lazy(
  () =>
    import("./components/instructions/google-calendar/GoogleCalendarSteps.js"),
);
const CalendarSteps = lazy(
  () => import("./components/instructions/other-calendars/CalendarSteps.js"),
);
const HowFreeTrialWorks = lazy(
  () => import("./components/instructions/free-trial/HowFreeTrialWorks.js"),
);
const GoogleCalendarConnected = lazy(
  () =>
    import("./components/instructions/google-calendar/GoogleCalendarConnected.js"),
);
const CalendarConnected = lazy(
  () =>
    import("./components/instructions/other-calendars/CalendarConnected.js"),
);
const ManageSubscription = lazy(
  () => import("./components/upgrades/consumer/ManageSubscription.js"),
);
const ChooseFreePlan = lazy(
  () => import("./components/instructions/free-plan/ChooseFreePlan.js"),
);
const CategoryPage = lazy(
  () => import("./components/discovery/pages/CategoryPage.js"),
);
const SeeAllPage = lazy(
  () => import("./components/discovery/pages/SeeAllPage.js"),
);
const TagPage = lazy(() => import("./components/discovery/pages/TagPage.js"));
const MovieGenrePage = lazy(() =>
  import("./components/discovery/pages/TagPage.js").then((m) => ({
    default: m.MovieGenrePage,
  })),
);
const Personalize = lazy(() => import("./components/Personalize.js"));
const PersonalizePlusPage = lazy(
  () => import("./components/PersonalizePlusPage.js"),
);
const ElectionsPage = lazy(() => import("./components/ElectionsPage.js"));
const PressPage = lazy(() => import("./components/PressPage.js"));
const ProductPage = lazy(() => import("./components/product/ProductPage.js"));
const ProductAdmin = lazy(() => import("./components/product/ProductAdmin.js"));
const CalendarSubscriptionExperience = lazy(
  () =>
    import("./components/instructions/free-calendar-subscription/CalendarSubscriptionExperience.js"),
);
const SuccessfulCalendarSubscription = lazy(
  () =>
    import("./components/instructions/free-calendar-subscription/SuccessfulCalendarSubscription.js"),
);
const SimpleVerifyCalendarSubscription = lazy(
  () =>
    import("./components/instructions/free-calendar-subscription/SimpleVerifyCalendarSubscription.js"),
);
const UpgradeTrigger = lazy(
  () => import("./components/instructions/UpgradeTrigger.js"),
);
const VerifyScreen = lazy(
  () =>
    import("./components/instructions/free-calendar-subscription/VerifyScreen.js"),
);
const HandleRedirect = lazyNamed(
  () => import("./components/routing/RedirectHandler.js"),
  "HandleRedirect",
);
const FunnelDashboard = lazy(() => import("./components/FunnelDashboard.js"));
const PerformanceAnalyticsDashboard = lazy(
  () => import("./components/admin/PerformanceAnalyticsDashboard.js"),
);
const PerformanceDashboard = lazy(
  () => import("./components/admin/PerformanceDashboard.js"),
);
const NcaaLinkDashboard = lazy(
  () => import("./components/admin/NcaaLinkDashboard.js"),
);
const PrintableSchedulesDashboard = lazy(
  () => import("./components/admin/PrintableSchedulesDashboard.js"),
);
const CreatorContactDashboard = lazy(
  () => import("./components/admin/CreatorContactDashboard.js"),
);
const CreatorAcquisitionDashboard = lazy(
  () => import("./components/admin/CreatorAcquisitionDashboard.js"),
);
const SectionsDashboard = lazy(
  () => import("./components/admin/SectionsDashboard.js"),
);
const AdminIndex = lazy(() => import("./components/admin/AdminIndex.js"));
const SeoDashboard = lazy(() => import("./components/admin/SeoDashboard.js"));
const CalendarTriageDashboard = lazy(() =>
  import("./components/admin/CalendarTriageDashboard.js"),
);
const GhostCampaignDashboard = lazy(
  () => import("./components/ghost-campaigns/GhostCampaignDashboard.js"),
);
const EmailSubscribe = lazy(() => import("./components/EmailSubscribe.js"));
const EmailPreferences = lazy(() => import("./components/EmailPreferences.js"));
const EventDefaults = lazy(() => import("./components/personalization/EventDefaults.js"));
const EasySubscribe = lazy(() => import("./components/EasySubscribe.js"));
const SubscribeConcierge = lazy(
  () => import("./components/instructions/concierge-subscription/SubscribeConcierge.js"),
);
const TrialActivationConcierge = lazy(
  () => import("./components/instructions/trial-activation/TrialActivationConcierge.js"),
);
const UpdateRelayEmail = lazy(() => import("./components/UpdateRelayEmail.js"));
const EventDetail = lazy(() => import("./components/EventDetail.js"));

function TimelineEmbeddedTest() {
  useEffect(() => {
    // The test route renders the custom element as raw HTML, so load the
    // module explicitly to register <stanza-timeline> before it is used.
    import("./components/TimelineEmbedded.js");
  }, []);

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: `
          <div style="width: 90%; height: 231px; border: 1px solid #D9D9D9; border-radius: 8px;">
            <stanza-timeline handle="nba-warriors" calendarId="53ae0d467f33ed8a06bd0421" direction="horizontal" loadAds="false"></stanza-timeline>
          </div>
          <div style="width: 300px; height: 700px; border: 1px solid #D9D9D9; border-radius: 8px; margin-top: 2rem;">
            <stanza-timeline handle="nba-warriors" calendarId="53ae0d467f33ed8a06bd0421" direction="vertical" loadAds="false"></stanza-timeline>
          </div>
        `,
      }}
    />
  );
}

const CreatorLanding = lazy(() => import("./components/CreatorLanding.js"));
const CreatorAnalyticsPage = lazy(
  () => import("./components/creatorLanding/CreatorAnalyticsPage.js"),
);
const CreatorInspirationPage = lazy(
  () => import("./components/creatorLanding/CreatorInspirationPage.js"),
);
const CreatorGetStartedPage = lazy(
  () => import("./components/creatorLanding/CreatorGetStartedPage.js"),
);
const ClaimExplainerPage = lazy(
  () => import("./components/creatorLanding/ClaimExplainerPage.js"),
);
const LeagueCaseStudy = lazy(
  () => import("./components/creatorLanding/caseStudies/LeagueCaseStudy.js"),
);
const WnbaCaseStudy = lazy(
  () => import("./components/creatorLanding/caseStudies/WnbaCaseStudy.js"),
);
const ProposalPage = lazy(
  () => import("./components/creatorLanding/caseStudies/ProposalPage.js"),
);
const PremiumProposalPage = lazy(
  () => import("./components/creatorLanding/caseStudies/PremiumProposalPage.js"),
);

/*
  Refetch on window focus or reconnect
  keep previous data until cacheTime.

  Built at module scope: a client created in App's body would be replaced on
  every render, dropping the cache that prefetched pages live in.
*/
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 10, // 10 minutes
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      onError: (error) => {
        console.error("Query error:", error);
      },
    },
  },
});

function App() {
  useAdjustViewport();

  // The inline detector exists only for the pre-React window Rollbar cannot
  // observe. Once React commits, clear its marker immediately: retaining it
  // while the user backgrounds a working app creates false crash positives.
  useEffect(() => {
    try {
      window.sessionStorage.removeItem("stz_load_pending");
    } catch (e) {
      // sessionStorage unavailable (private mode edge cases) — nothing to clear.
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <FeatureAccessProvider>
          <PreventBackButton />
          <NavigationManager />
          <BackNavigationPosition />
          <ScrollToTop />
          <RedirectSignInForward />
          <CalendarLimitInterstitial />
          <CalendarReviewInterstitial />
          <GoogleConnectInactiveInterstitial />
          <GoogleConnectInterceptHost />
          <EmailLinkConfirmDialog />
          <Suspense fallback={<LoadingAnimation />}>
            <Routes>
              <Route
                path={getPageRoute("home", "HOME")}
                element={withShell(<DiscoveryHome />)}
              />
              <Route
                path={getPageRoute("event", "EVENT_DETAIL")}
                element={withShell(<EventDetail />)}
              />
              <Route
                path={getPageRoute("home", "TAG")}
                element={withShell(<TagPage key={window.location.pathname} />)}
              />
              <Route
                path={getPageRoute("home", "MOVIE_GENRE")}
                element={withShell(
                  <MovieGenrePage key={window.location.pathname} />,
                )}
              />
              <Route
                path={getPageRoute("home", "EXPLORE_CATEGORY")}
                element={withShell(<CategoryPage />)}
              />
              <Route
                path={getPageRoute("home", "SECTION_SEE_ALL")}
                element={withShell(<SeeAllPage />)}
              />
              <Route
                path={getPageRoute("home", "PERSONALIZE")}
                element={<Personalize />}
              />
              <Route
                path={getPageRoute("home", "PERSONALIZATION")}
                element={withShell(<PersonalizePlusPage />, {
                  variant: "dark",
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={getPageRoute("home", "VISION")}
                element={withShell(<Vision />)}
              />
              <Route
                path={getPageRoute("elections", "US_ELECTIONS")}
                element={withShell(<ElectionsPage />, {
                  variant: "dark",
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={`${getPageRoute("elections", "US_ELECTIONS")}/:calendarId`}
                element={
                  <Navigate
                    to={getPageRoute("elections", "US_ELECTIONS")}
                    replace
                  />
                }
              />
              <Route
                path="/press/:slug"
                element={withShell(<PressPage />, {
                  variant: "dark",
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path="/product/:slug"
                element={withShell(<ProductPage />, {
                  variant: "dark",
                  disableGutters: true,
                })}
              />
              <Route
                path={getPageRoute("seo", "PRODUCT_ADMIN")}
                element={withShell(<ProductAdmin />, {
                  sidebar: false,
                  bottomTabBar: false,
                  footer: "none",
                  disableGutters: true,
                })}
              />
              <Route
                path="/creators"
                element={withShell(<CreatorLanding />, {
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={getPageRoute("creator", "CREATOR_ANALYTICS")}
                element={withShell(<CreatorAnalyticsPage />, {
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={getPageRoute("creator", "CREATOR_GET_STARTED")}
                element={withShell(<CreatorGetStartedPage />, {
                  variant: "dark",
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={getPageRoute("creator", "CREATOR_INSPIRATION")}
                element={withShell(<CreatorInspirationPage />, {
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path={getPageRoute("creator", "CREATOR_CLAIM_EXPLAINER")}
                element={withShell(<ClaimExplainerPage />, {
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              <Route
                path="/creators/case-studies/wnba"
                element={withShell(<WnbaCaseStudy />, {
                  variant: "dark",
                  disableGutters: true,
                  footer: "expanded-dark",
                })}
              />
              {/* Retired standalone pitches: proposals pages replace them. */}
              <Route
                path="/creators/case-studies/maryland"
                element={<Navigate to="/creators/proposals/sports/ncaa/umterps" replace />}
              />
              <Route
                path="/creators/case-studies/sidearm"
                element={<Navigate to="/creators/proposals/platforms/sidearm" replace />}
              />
              <Route
                path={getPageRoute("creator", "CASE_STUDY")}
                element={withShell(
                  <LeagueCaseStudy key={window.location.pathname} />,
                  {
                    variant: "dark",
                    disableGutters: true,
                    footer: "expanded-dark",
                  },
                )}
              />
              <Route
                path={getPageRoute("creator", "PROPOSAL_SPORTS")}
                element={withShell(
                  <ProposalPage key={window.location.pathname} />,
                  {
                    variant: "dark",
                    disableGutters: true,
                    footer: "expanded-dark",
                  },
                )}
              />
              <Route
                path={getPageRoute("creator", "PROPOSAL_NCAA")}
                element={withShell(
                  <ProposalPage key={window.location.pathname} variant="ncaa" />,
                  {
                    variant: "dark",
                    disableGutters: true,
                    footer: "expanded-dark",
                  },
                )}
              />
              <Route
                path={getPageRoute("creator", "PROPOSAL_PLATFORM")}
                element={withShell(
                  <ProposalPage key={window.location.pathname} variant="platform" />,
                  {
                    variant: "dark",
                    disableGutters: true,
                    footer: "expanded-dark",
                  },
                )}
              />
              <Route
                path={getPageRoute("creator", "PROPOSAL_PREMIUM")}
                element={withShell(
                  <PremiumProposalPage key={window.location.pathname} />,
                  {
                    variant: "dark",
                    disableGutters: true,
                    footer: "expanded-dark",
                  },
                )}
              />
              <Route path="/subscribe/:handle" element={<EasySubscribe />} />
              <Route
                path={getPageRoute("chat", "SEARCH")}
                element={withShell(<Search />, {
                  disableGutters: true,
                  bottomTabBar: false,
                  footer: "none",
                })}
              />
              <Route
                path={getPageRoute("chat", "SEARCH_TAB")}
                element={withShell(<Search />, {
                  disableGutters: true,
                  bottomTabBar: false,
                  footer: "none",
                })}
              />
              <Route
                path={getPageRoute("calendar", "LEGACY_TIMELINE")}
                element={withShell(<Timeline />)}
              />
              <Route
                path={getPageRoute("calendar", "LEGACY_SUBSCRIPTION")}
                element={withShell(<Timeline />)}
              />
              <Route
                path={getPageRoute("calendar", "LEGACY_SCHEDULES")}
                element={withShell(<Timeline />)}
              />
              <Route
                path={getPageRoute("calendar", "ALL_EVENTS")}
                element={withShell(<Timeline />)}
              />
              <Route
                path={getPageRoute("calendar", "CALENDAR_EVENTS")}
                element={withShell(<Timeline />)}
              />
              <Route
                path="/march-madness"
                element={<Navigate to="/mm-mens-full-tournament" replace />}
              />
              <Route
                path="/event/:id"
                element={
                  <Navigate to="/" state={{ calendarNotFound: true }} replace />
                }
              />
              <Route path="/button/embed" element={<HandleRedirect />} />
              <Route path="/button/launch" element={<HandleRedirect />} />
              <Route
                path={getPageRoute("calendar", "GROUP_EVENTS")}
                element={withShell(<Timeline />)}
              />

              {/* --- 🕴️ CREATOR ROUTES --- */}
              <Route
                path={getPageRoute("creator", "ONBOARDING_ADD_LINK")}
                element={
                  <ProtectedRoute Component={CreatorOnboardingAddLink} />
                }
              />
              <Route
                path={getPageRoute("creator", "ONBOARDING_VERIFY")}
                element={<ProtectedRoute Component={CreatorOnboardingVerify} />}
              />
              <Route
                path={getPageRoute("calendar", "PREVIEW_CALENDAR")}
                element={withShell(<Timeline preview={true} />)}
              />
              <Route
                path={getPageRoute("calendar", "PREVIEW_CALENDAR_EVENTS")}
                element={withShell(<Timeline preview={true} />)}
              />
              <Route
                path={getPageRoute("calendar", "CALENDAR_SETTINGS")}
                element={withShell(
                  <ProtectedRoute Component={CalendarSettings} />,
                  { footer: "minimal" },
                )}
              />
              <Route
                path={getPageRoute("help", "HELP_IMPORT")}
                element={withShell(<ProtectedRoute Component={HowToImport} />, {
                  footer: "minimal",
                })}
              />
              <Route
                path={getPageRoute("help", "CONCIERGE")}
                element={<ProtectedRoute Component={SubscribeConcierge} />}
              />
              <Route
                path={getPageRoute("calendar", "IMPORT")}
                element={withShell(
                  <ProtectedRoute Component={ImportCalendar} />,
                  { footer: "minimal" },
                )}
              />
              <Route
                path={getPageRoute("auth", "SIGNIN")}
                element={<SignIn />}
              />
              <Route
                path={getPageRoute("library", "LIBRARY")}
                element={withShell(<ProtectedRoute Component={Library} />, {
                  footer: "minimal",
                })}
              />
              <Route
                path={getPageRoute("upgrade", "MANAGE_SUBSCRIPTION")}
                element={<ProtectedRoute Component={ManageSubscription} />}
              />

              {/* --- 📅 OTHER CALENDAR ROUTES --- */}
              <Route
                path={getPageRoute("help", "SUBSCRIBING_CALENDAR")}
                element={<ProtectedRoute Component={CalendarExperience} />}
              />

              <Route
                path={getPageRoute("help", "SUBSCRIBING_CALENDAR_STEPS")}
                element={<ProtectedRoute Component={CalendarSteps} />}
              />
              <Route
                path={getPageRoute(
                  "help",
                  "SUBSCRIBING_CALENDAR_STEPS_STANDALONE",
                )}
                element={<ProtectedRoute Component={CalendarSteps} />}
              />

              <Route
                path={getPageRoute("help", "CALENDAR_CONNECTED")}
                element={<ProtectedRoute Component={CalendarConnected} />}
              />

              {/* --- 📆 GOOGLE CALENDAR ROUTES --- */}
              <Route
                path={getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR")}
                element={
                  <ProtectedRoute Component={GoogleCalendarExperience} />
                }
              />
              <Route
                path={getPageRoute("help", "SUBSCRIBING_GOOGLE_CALENDAR_STEPS")}
                element={<ProtectedRoute Component={GoogleCalendarSteps} />}
              />
              <Route
                path={getPageRoute(
                  "help",
                  "SUBSCRIBING_GOOGLE_CALENDAR_STEPS_STANDALONE",
                )}
                element={<ProtectedRoute Component={GoogleCalendarSteps} />}
              />
              <Route
                path={getPageRoute("help", "CONNECT_GOOGLE_CALENDAR")}
                element={<ProtectedRoute Component={ConnectGoogleCalendar} />}
              />
              <Route
                path={getPageRoute("help", "GOOGLE_CONNECT_OFF")}
                element={<ProtectedRoute Component={TurnOffGoogleSync} />}
              />
              <Route
                path={getPageRoute("help", "GOOGLE_CALENDAR_CONNECTED")}
                element={<ProtectedRoute Component={GoogleCalendarConnected} />}
              />
              <Route
                path={getPageRoute("auth", "GOOGLE_CALENDAR_CALLBACK")}
                element={<ProtectedRoute Component={GoogleCalendarCallback} />}
              />

              {/* --- 📅 SUBSCRIBING CALENDAR ROUTES --- */}
              <Route
                path={getPageRoute("help", "SUBSCRIBING_CALENDAR_EXPERIENCE")}
                element={
                  <ProtectedRoute Component={CalendarSubscriptionExperience} />
                }
              />
              <Route
                path={getPageRoute(
                  "help",
                  "SUBSCRIBING_CALENDAR_VERIFY_SCREEN",
                )}
                element={<ProtectedRoute Component={VerifyScreen} />}
              />
              <Route
                path={getPageRoute("help", "SUBSCRIBING_CALENDAR_VERIFY")}
                element={<SimpleVerifyCalendarSubscription />}
              />
              <Route
                path={getPageRoute("help", "SUBSCRIBING_CALENDAR_SUCCESS")}
                element={
                  <ProtectedRoute Component={SuccessfulCalendarSubscription} />
                }
              />

              {/* --- 🚀 UPGRADE TRIGGER ROUTES --- */}
              <Route
                path={getPageRoute("upgrade", "UPGRADE_TRIGGER")}
                element={<ProtectedRoute Component={UpgradeTrigger} />}
              />

              {/* --- 🚀 UPGRADE ROUTES --- */}
              <Route
                path={getPageRoute("upgrade", "UPGRADE_PLUS")}
                element={withShell(<ConsumerUpgrade />, { footer: "minimal" })}
              />
              <Route
                path={getPageRoute("upgrade", "MEMBERSHIP")}
                element={withShell(<MembershipPage />, { footer: "minimal" })}
              />
              <Route
                path="/upgrade/creator"
                element={<Navigate to="/creators" replace />}
              />
              <Route
                path="/upgrade/creator-mailing-list"
                element={<Navigate to="/creators" replace />}
              />
              <Route
                path={getPageRoute(
                  "upgrade",
                  "PAYMENT_STANZA_PLUS_CONFIRMATION",
                )}
                element={<ProtectedRoute Component={TrialActivationConcierge} />}
              />
              <Route
                path={getPageRoute("help", "FREE_TRIAL_STEPS")}
                element={<ProtectedRoute Component={HowFreeTrialWorks} />}
              />

              {/* --- 🆓 FREE PLAN ROUTES --- */}
              <Route
                path={getPageRoute("help", "CHOOSE_FREE_PLAN")}
                element={<ProtectedRoute Component={ChooseFreePlan} />}
              />

              {/* --- 📊 ANALYTICS ROUTES --- */}
              <Route
                path={getPageRoute("analytics", "FUNNEL_DASHBOARD")}
                element={<ProtectedRoute Component={FunnelDashboard} />}
              />
              <Route
                path={getPageRoute("analytics", "PERFORMANCE_DASHBOARD")}
                element={
                  <ProtectedRoute Component={PerformanceAnalyticsDashboard} />
                }
              />
              <Route
                path={getPageRoute("ghostCampaigns", "CAMPAIGN_DASHBOARD")}
                element={<ProtectedRoute Component={GhostCampaignDashboard} />}
              />

              {/* --- NCAA link-repair dashboard (sign-in + email allowlist) --- */}
              <Route
                path={getPageRoute("ncaaLinks", "NCAA_DASHBOARD")}
                element={<ProtectedRoute Component={NcaaLinkDashboard} />}
              />

              {/* --- Printable schedule uploads for any calendar (sign-in + @stanza.co) --- */}
              <Route
                path={getPageRoute("printableSchedules", "PRINTABLE_SCHEDULE_DASHBOARD")}
                element={<ProtectedRoute Component={PrintableSchedulesDashboard} />}
              />

              {/* --- Conversion performance dashboard (sign-in + admin) --- */}
              <Route
                path={getPageRoute("performance", "PERFORMANCE_DASHBOARD")}
                element={<ProtectedRoute Component={PerformanceDashboard} />}
              />

              {/* --- SEO triage dashboard (sign-in + @stanza.co) --- */}
              <Route
                path={getPageRoute("seo", "SEO_DASHBOARD")}
                element={<ProtectedRoute Component={SeoDashboard} />}
              />

              {/* --- Calendar triage dashboard (sign-in + @stanza.co) --- */}
              <Route
                path={getPageRoute("calendarTriage", "CALENDAR_TRIAGE_DASHBOARD")}
                element={<ProtectedRoute Component={CalendarTriageDashboard} />}
              />

              {/* --- Creator-email entry dashboard (sign-in + contractor allowlist) --- */}
              <Route
                path={getPageRoute(
                  "creatorContacts",
                  "CREATOR_CONTACT_DASHBOARD",
                )}
                element={<ProtectedRoute Component={CreatorContactDashboard} />}
              />

              {/* --- Creator-acquisition operating dashboard (internal @stanza.co) --- */}
              <Route
                path={getPageRoute(
                  "creatorAcquisition",
                  "CREATOR_ACQUISITION_DASHBOARD",
                )}
                element={
                  <ProtectedRoute Component={CreatorAcquisitionDashboard} />
                }
              />

              {/* --- Section catalog admin dashboard (sign-in + contractor allowlist) --- */}
              <Route
                path={getPageRoute("sectionsAdmin", "SECTIONS_DASHBOARD")}
                element={<ProtectedRoute Component={SectionsDashboard} />}
              />

              {/* --- Admin index: directory of all internal ops dashboards --- */}
              <Route
                path="/admin"
                element={<ProtectedRoute Component={AdminIndex} />}
              />

              {/* --- Email magic-link subscribe (token redemption + chat-style add-to-calendar) --- */}
              <Route
                path={getPageRoute("auth", "EMAIL_SUBSCRIBE")}
                element={<EmailSubscribe />}
              />

              {/* --- Email preference center (public; identity via session/token/uid) --- */}
              <Route
                path={getPageRoute("user", "EMAIL_PREFERENCES")}
                element={<EmailPreferences />}
              />

              {isCalendarPersonalizationEnabled() && (
                <Route
                  path={getPageRoute("user", "EVENT_DEFAULTS")}
                  element={<ProtectedRoute Component={EventDefaults} />}
                />
              )}

              {/* --- Apple Private Relay migration landing (token-auth via ?token, no session) --- */}
              <Route
                path={getPageRoute("user", "UPDATE_RELAY_EMAIL")}
                element={<UpdateRelayEmail />}
              />

              {/* --- 📄 TERMS & PRIVACY ROUTES --- */}
              <Route
                path={getPageRoute("privacy", "PRIVACY")}
                element={withShell(<Privacy />)}
              />
              <Route
                path={getPageRoute("terms", "TERMS")}
                element={withShell(<Terms />)}
              />
              <Route
                path={getPageRoute("embedded", "EMBEDDED_TIMELINE_EVENTS")}
                element={<TimelineEmbedded />}
              />
              {/* Test route for embedded timeline (debug webcomponent locally) */}
              {process.env.NODE_ENV !== "production" && (
                <Route
                  path="/timeline_embedded_test"
                  element={<TimelineEmbeddedTest />}
                />
              )}
              {/* Test route for embedded timeline (debug iframe locally) */}
              {process.env.NODE_ENV !== "production" && (
                <Route
                  path="/timeline_embedded_iframe_test"
                  element={
                    <>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: `
                <!-- cross-iframe of embedded timeline page -->      
                <iframe style="width: 100%; height: 250px; border: 1px solid #D9D9D9; border-radius: 8px;" src="https://127.0.0.1:3000/embedded/nba-warriors/53ae0d467f33ed8a06bd0421?direction=horizontal"></iframe>
                <!-- cross-amp-iframe of stanza-timeline -->    
                <amp-iframe width="100%" height="800px" layout="fixed" frameborder="0" src="https://127.0.0.1:3000/timeline_embedded_test" i-amphtml-layout="fixed" style="width: 100%; height: 800px; display: block; margin-top: 2rem;"><i-amphtml-scroll-container class="amp-active"><iframe style="height: 100%; width: 100%;" name="amp_iframe0" frameborder="0" allow="" sandbox="allow-scripts allow-same-origin" src="https://127.0.0.1:3000/timeline_embedded_test"></iframe></i-amphtml-scroll-container></amp-iframe>
                `,
                        }}
                      />
                    </>
                  }
                />
              )}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </FeatureAccessProvider>
      </Router>
      {/*     <ReactQueryDevtools initialIsOpen={false} />*/}
    </QueryClientProvider>
  );
}

export default App;
